import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";

const TOKEN_KEY = "bbf_admin_token";

function authHeaders() {
  const t = localStorage.getItem(TOKEN_KEY);
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function downscaleImage(dataUrl, maxW = 1600, quality = 0.85) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1, maxW / img.width);
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

export default function Admin() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  if (!token) {
    return <Login onLogin={(t) => { localStorage.setItem(TOKEN_KEY, t); setToken(t); }} />;
  }
  return (
    <Dashboard
      onLogout={() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      }}
    />
  );
}

function Login({ onLogin }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      onLogin(data.token);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Не удалось войти");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505] text-white">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md p-10 border border-white/10">
        <p className="overline">Big Bag Films · Admin</p>
        <h1 className="font-display uppercase text-4xl tracking-tight mt-3">Вход</h1>
        <label className="block mt-10">
          <span className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/40">Пароль</span>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="mt-3 w-full bg-transparent border-0 border-b border-white/15 focus:border-[#a78bfa] outline-none px-0 py-3 text-xl"
          />
        </label>
        <button
          data-testid="admin-login-submit"
          type="submit"
          disabled={loading}
          className="bbf-btn-primary mt-10 px-6 py-4 uppercase text-[11px] tracking-[0.22em] font-bold disabled:opacity-60"
        >
          {loading ? "Входим…" : "Войти"}
        </button>
        <a href="/" className="mt-6 block bbf-link text-white/50 text-[11px] uppercase tracking-[0.22em]">
          ← На сайт
        </a>
      </form>
    </div>
  );
}

const TABS = [
  { id: "site",          label: "Тексты" },
  { id: "structure",     label: "Структура" },
  { id: "projects",      label: "Проекты" },
  { id: "articles",      label: "Статьи" },
  { id: "team",          label: "Команда" },
  { id: "clients",       label: "Клиенты" },
  { id: "testimonials",  label: "Отзывы" },
  { id: "invitations",   label: "Заявки на премьеру" },
  { id: "callbacks",     label: "Заявки на звонок" },
];

function Dashboard({ onLogout }) {
  const [tab, setTab] = useState("site");
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-black/80 backdrop-blur-xl z-50">
        <a href="/" className="font-display uppercase tracking-tight text-sm">Big Bag Films · Admin</a>
        <div className="flex items-center gap-3">
          <a href="/" className="bbf-link text-[11px] uppercase tracking-[0.22em] text-white/60">На сайт</a>
          <button data-testid="admin-logout" onClick={onLogout} className="px-4 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black">
            Выйти
          </button>
        </div>
      </header>

      <nav className="border-b border-white/10 px-6 flex gap-2 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-3 font-mono-jb text-[11px] uppercase tracking-[0.22em] whitespace-nowrap ${
              tab === t.id ? "text-white border-b-2 border-[#a78bfa]" : "text-white/50 border-b-2 border-transparent"
            }`}
          >
            {t.label}
          </button>
        ))}
      </nav>

      <main className="p-6 lg:p-10 max-w-6xl mx-auto">
        {tab === "site" && <SiteEditor />}
        {tab === "structure" && <StructureEditor />}
        {tab === "projects" && (
          <CrudEditor
            kind="projects"
            title="Проекты"
            fields={[
              { name: "kind",        label: "Тип",
                options: [
                  { value: "video",    label: "Видео-работа (превью + видео)" },
                  { value: "object3d", label: "3D-объект (PNG без фона, декоративный)" },
                ] },
              { name: "title",       label: "Заголовок", required: true },
              { name: "client",      label: "Клиент",    required: true },
              { name: "category",    label: "Категория (Имидж / Brand / CG / AI / Live и т.п.)", required: true },
              { name: "year",        label: "Год",       required: true },
              { name: "description", label: "Описание",  multiline: true },
              { name: "cover_url",   label: "Обложка / превью. Для 3D-объекта: прозрачный PNG, GIF (зациклен авто) или MP4/WebM (зациклен авто)", image: true },
              { name: "video_url",   label: "Ссылка на полное видео (Vimeo / YouTube / прямой MP4)" },
              { name: "article_slug", label: "Slug внутренней статьи (если указан — клик ведёт на /article/slug)" },
              { name: "accent",      label: "Размер карточки",
                options: [
                  { value: "default", label: "Стандарт" },
                  { value: "wide",    label: "Широкая" },
                  { value: "tall",    label: "Вертикальная" },
                  { value: "large",   label: "Большая" },
                ] },
              { name: "parallax_speed", label: "Скорость параллакса (-1..+1, 0 = авто)", type: "number" },
              { name: "order",       label: "Порядок (число)", type: "number" },
            ]}
          />
        )}
        {tab === "articles" && <ArticlesEditor />}
        {tab === "team" && (
          <CrudEditor
            kind="team"
            title="Команда"
            fields={[
              { name: "name",      label: "Имя",       required: true },
              { name: "role",      label: "Роль",      required: true },
              { name: "note",      label: "Подпись",   multiline: true },
              { name: "photo_url", label: "Фото",      image: true },
              { name: "order",     label: "Порядок",   type: "number" },
            ]}
          />
        )}
        {tab === "clients" && (
          <CrudEditor
            kind="clients"
            title="Клиенты (бегущая строка)"
            fields={[
              { name: "name",     label: "Название", required: true },
              { name: "logo_url", label: "Логотип (PNG, белый/прозрачный)", image: true },
              { name: "link_url", label: "Ссылка при клике (URL)" },
              { name: "row",      label: "Строка (0, 1, 2)", type: "number" },
              { name: "order",    label: "Порядок", type: "number" },
            ]}
          />
        )}
        {tab === "testimonials" && (
          <CrudEditor
            kind="testimonials"
            title="Отзывы"
            fields={[
              { name: "client",    label: "Клиент",   required: true },
              { name: "author",    label: "Автор",    required: true },
              { name: "quote",     label: "Цитата",   required: true, multiline: true },
              { name: "video_url", label: "Видео-отзыв (Vimeo / YouTube / MP4) — откроется в плеере" },
              { name: "cover_url", label: "Постер для видео (необязательно)", image: true },
              { name: "order",     label: "Порядок", type: "number" },
            ]}
          />
        )}
        {tab === "invitations" && <InvitationsList />}
        {tab === "callbacks" && <CallbacksList />}
      </main>
    </div>
  );
}

function SiteEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/site`, { headers: authHeaders() })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Не удалось загрузить настройки"));
  }, []);

  const update = (k) => (e) => {
    const v = e.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: v }));
  };

  const updateStep = (i, val) => {
    setData((d) => {
      const steps = [...(d.method_steps || [])];
      steps[i] = val;
      return { ...d, method_steps: steps };
    });
  };
  const addStep = () => setData((d) => ({ ...d, method_steps: [...(d.method_steps || []), "Новый шаг"] }));
  const removeStep = (i) => setData((d) => {
    const steps = [...(d.method_steps || [])];
    steps.splice(i, 1);
    return { ...d, method_steps: steps };
  });

  const updateStat = (i, key, val) => {
    setData((d) => {
      const stats = [...(d.stats || [])];
      stats[i] = { ...stats[i], [key]: val };
      return { ...d, stats };
    });
  };
  const addStat = () => setData((d) => ({ ...d, stats: [...(d.stats || []), { value: "", label: "" }] }));
  const removeStat = (i) => setData((d) => {
    const stats = [...(d.stats || [])];
    stats.splice(i, 1);
    return { ...d, stats };
  });

  const updateSkill = (i, key, val) => {
    setData((d) => {
      const skills = [...(d.skills_proof || [])];
      skills[i] = { ...skills[i], [key]: val };
      return { ...d, skills_proof: skills };
    });
  };
  const addSkill = () => setData((d) => ({
    ...d,
    skills_proof: [...(d.skills_proof || []), { title: "", text: "", image: "", link: "#work" }],
  }));
  const removeSkill = (i) => setData((d) => {
    const skills = [...(d.skills_proof || [])];
    skills.splice(i, 1);
    return { ...d, skills_proof: skills };
  });

  const onSkillFile = async (i, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Файл больше 8 МБ"); return; }
    const dataUrl = await fileToDataURL(file);
    const small = await downscaleImage(dataUrl, 1600, 0.85);
    updateSkill(i, "image", small);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site`, data, { headers: authHeaders() });
      toast.success("Сохранено");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <p className="text-white/50">Загружаем…</p>;

  const stringFields = [
    { k: "hero_kicker", l: "Hero kicker" },
    { k: "hero_headline", l: "Hero заголовок", multi: true },
    { k: "hero_sub", l: "Hero подзаголовок", multi: true },
    { k: "hero_phase_2_kicker", l: "Hero фаза 2 — kicker" },
    { k: "hero_phase_2_headline", l: "Hero фаза 2 — заголовок", multi: true },
    { k: "hero_phase_3_kicker", l: "Hero фаза 3 — kicker" },
    { k: "hero_phase_3_headline", l: "Hero фаза 3 — заголовок", multi: true },
    { k: "showreel_url", l: "Шоурил URL — открывается по клику на заголовок (vimeo/youtube/mp4)" },
    { k: "discuss_button", l: "Кнопка «Обсудить проект»" },
    { k: "social_vimeo",    l: "Соцсеть — Vimeo URL" },
    { k: "social_behance",  l: "Соцсеть — Behance URL" },
    { k: "social_telegram", l: "Соцсеть — Telegram URL" },
    { k: "method_title", l: "Метод — заголовок" },
    { k: "method_subtitle", l: "Метод — подзаголовок" },
    { k: "impressions_title", l: "О нас — заголовок", multi: true },
    { k: "impressions_body", l: "О нас — описание", multi: true },
    { k: "skills_title",    l: "Доказательства — заголовок" },
    { k: "skills_subtitle", l: "Доказательства — подпись", multi: true },
    { k: "team_title", l: "Команда — заголовок" },
    { k: "clients_title", l: "Клиенты — заголовок" },
    { k: "premiere_kicker", l: "Премьера — kicker" },
    { k: "premiere_headline", l: "Премьера — заголовок", multi: true },
    { k: "premiere_lead", l: "Премьера — описание", multi: true },
    { k: "premiere_button", l: "Премьера — кнопка" },
    { k: "contact_email", l: "Контактный email" },
    { k: "contact_site", l: "Сайт (без https)" },
    { k: "contact_vimeo", l: "Vimeo ссылка (footer)" },
  ];

  return (
    <div data-testid="admin-site-editor" className="space-y-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-3xl">Тексты лендинга</h2>
        <button data-testid="admin-site-save" onClick={save} disabled={saving} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold">
          {saving ? "Сохраняем…" : "Сохранить"}
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {stringFields.map(({ k, l, multi }) => (
          <label key={k} className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">{l}</span>
            {multi ? (
              <textarea rows={3} value={data[k] || ""} onChange={update(k)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3 text-white" />
            ) : (
              <input value={data[k] || ""} onChange={update(k)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3 text-white" />
            )}
          </label>
        ))}
      </div>

      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-display uppercase text-2xl">Шаги метода</h3>
          <button onClick={addStep} className="text-[11px] uppercase tracking-[0.22em] border border-white/15 px-3 py-2 hover:bg-white hover:text-black">+ шаг</button>
        </div>
        <div className="mt-4 space-y-2">
          {(data.method_steps || []).map((s, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="font-mono-jb text-[10px] tracking-[0.22em] text-white/40 w-8">{String(i + 1).padStart(2, "0")}</span>
              <input value={s} onChange={(e) => updateStep(i, e.target.value)} className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
              <button onClick={() => removeStep(i)} className="text-[11px] uppercase tracking-[0.22em] text-red-400 px-3 py-2 hover:bg-red-500/10">×</button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between">
          <h3 className="font-display uppercase text-2xl">Статистика (О нас)</h3>
          <button onClick={addStat} className="text-[11px] uppercase tracking-[0.22em] border border-white/15 px-3 py-2 hover:bg-white hover:text-black">+ цифра</button>
        </div>
        <div className="mt-4 space-y-2">
          {(data.stats || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-3">
              <input value={s.value || ""} onChange={(e) => updateStat(i, "value", e.target.value)} placeholder="10+" className="col-span-3 bg-[#0a0a0a] border border-white/10 rounded-sm p-3 font-display" />
              <input value={s.label || ""} onChange={(e) => updateStat(i, "label", e.target.value)} placeholder="лет на рынке" className="col-span-8 bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
              <button onClick={() => removeStat(i)} className="col-span-1 text-[11px] uppercase tracking-[0.22em] text-red-400 hover:bg-red-500/10">×</button>
            </div>
          ))}
        </div>
      </section>

      <section data-testid="admin-skills-editor">
        <div className="flex items-center justify-between">
          <h3 className="font-display uppercase text-2xl">Доказательства навыков (3 баннера)</h3>
          <button onClick={addSkill} className="text-[11px] uppercase tracking-[0.22em] border border-white/15 px-3 py-2 hover:bg-white hover:text-black">+ баннер</button>
        </div>
        <div className="mt-4 space-y-4">
          {(data.skills_proof || []).map((s, i) => (
            <div key={i} className="grid grid-cols-12 gap-3 border border-white/10 p-4">
              <div className="col-span-12 sm:col-span-3">
                {s.image ? (
                  <img src={s.image} alt="" className="w-full aspect-[4/5] object-cover border border-white/10" />
                ) : (
                  <div className="w-full aspect-[4/5] bg-[#101010] border border-white/10" />
                )}
                <label className="mt-2 block">
                  <input type="file" accept="image/*" onChange={(e) => onSkillFile(i, e.target.files?.[0])} className="hidden" id={`skill-file-${i}`} />
                  <span
                    onClick={() => document.getElementById(`skill-file-${i}`)?.click()}
                    className="block text-center px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em] cursor-pointer hover:bg-white hover:text-black"
                  >
                    Загрузить
                  </span>
                </label>
                <input value={s.image || ""} onChange={(e) => updateSkill(i, "image", e.target.value)} placeholder="…или URL" className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-2 text-xs" />
              </div>
              <div className="col-span-12 sm:col-span-8 space-y-2">
                <input value={s.title || ""} onChange={(e) => updateSkill(i, "title", e.target.value)} placeholder="Заголовок" className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3 font-display uppercase" />
                <textarea rows={3} value={s.text || ""} onChange={(e) => updateSkill(i, "text", e.target.value)} placeholder="Краткое описание" className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
                <input value={s.link || ""} onChange={(e) => updateSkill(i, "link", e.target.value)} placeholder="Ссылка для «подробнее» (#work, /article/slug, https://… )" className="w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3 text-sm" />
              </div>
              <button onClick={() => removeSkill(i)} className="col-span-12 sm:col-span-1 text-[11px] uppercase tracking-[0.22em] text-red-400 hover:bg-red-500/10">×</button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const blankFromFields = (fields) =>
  fields.reduce((acc, f) => {
    if (f.type === "number") acc[f.name] = 0;
    else acc[f.name] = "";
    return acc;
  }, {});

function CrudEditor({ kind, title, fields }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/${kind}`, { headers: authHeaders() });
      setItems(data);
    } catch {
      toast.error("Не удалось загрузить");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => setEditing({ ...blankFromFields(fields), _new: true });

  const remove = async (id) => {
    if (!window.confirm("Удалить?")) return;
    await axios.delete(`${API}/admin/${kind}/${id}`, { headers: authHeaders() });
    toast.success("Удалено");
    load();
  };

  return (
    <div data-testid={`admin-crud-${kind}`} className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display uppercase text-3xl">{title}</h2>
        <button data-testid={`admin-${kind}-new`} onClick={startNew} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold">
          + Добавить
        </button>
      </div>

      <ul className="divide-y divide-white/10 border border-white/10">
        {items.length === 0 && <li className="p-6 text-white/40">Ещё нет записей.</li>}
        {items.map((it) => (
          <li key={it.id} className="p-4 flex items-center gap-4">
            <Thumb item={it} fields={fields} />
            <div className="flex-1 min-w-0">
              <p className="font-display uppercase truncate">
                {it.title || it.name || it.client || "—"}
              </p>
              <p className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/40 truncate">
                {it.client && it.title ? `${it.client} · ${it.year || ""}` : it.role || it.author || ""}
              </p>
            </div>
            <button onClick={() => setEditing(it)} className="px-3 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black">Изм.</button>
            <button onClick={() => remove(it.id)} className="px-3 py-2 border border-red-500/30 text-red-300 text-[11px] uppercase tracking-[0.22em] hover:bg-red-500 hover:text-white">×</button>
          </li>
        ))}
      </ul>

      {editing && (
        <EditDialog
          fields={fields}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          kind={kind}
        />
      )}
    </div>
  );
}

function Thumb({ item, fields }) {
  const imgField = fields.find((f) => f.image);
  const url = imgField ? item[imgField.name] : null;
  if (!url) return <div className="w-16 h-16 bg-[#101010] border border-white/10" />;
  return <img src={url} alt="" className="w-16 h-16 object-cover border border-white/10" />;
}

function EditDialog({ fields, item, onClose, onSaved, kind }) {
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const update = (name, v) => setForm((f) => ({ ...f, [name]: v }));

  const onFile = async (name, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("Файл больше 8 МБ");
      return;
    }
    const dataUrl = await fileToDataURL(file);
    const small = await downscaleImage(dataUrl, 1600, 0.85);
    update(name, small);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const f of fields) {
        let v = form[f.name];
        if (f.type === "number") v = Number(v) || 0;
        payload[f.name] = v ?? (f.type === "number" ? 0 : "");
      }
      if (form._new || !form.id) {
        await axios.post(`${API}/admin/${kind}`, payload, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/admin/${kind}/${form.id}`, payload, { headers: authHeaders() });
      }
      toast.success("Сохранено");
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Ошибка");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 max-w-2xl w-full p-8 my-8" data-testid="admin-edit-dialog">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-xl">{form._new ? "Новая запись" : "Редактировать"}</h3>
          <button onClick={onClose} className="text-white/60">×</button>
        </div>
        <div className="space-y-5">
          {fields.map((f) => (
            <label key={f.name} className="block">
              <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">
                {f.label}{f.required && <span className="text-[#a78bfa]"> *</span>}
              </span>
              {f.image ? (
                <ImageField value={form[f.name]} onValue={(v) => update(f.name, v)} onFile={(file) => onFile(f.name, file)} />
              ) : f.options ? (
                <select value={form[f.name] || ""} onChange={(e) => update(f.name, e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3">
                  {f.options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              ) : f.multiline ? (
                <textarea rows={4} value={form[f.name] || ""} onChange={(e) => update(f.name, e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
              ) : (
                <input type={f.type || "text"} value={form[f.name] ?? ""} onChange={(e) => update(f.name, e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
              )}
            </label>
          ))}
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 border border-white/15 text-[11px] uppercase tracking-[0.22em]">Отмена</button>
          <button onClick={save} disabled={saving} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold disabled:opacity-60">
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ImageField({ value, onValue, onFile }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="mt-2 space-y-3">
      {value ? (
        <div className="flex items-center gap-4">
          <img src={value} alt="" className="w-32 h-32 object-cover border border-white/15" />
          <button onClick={() => onValue("")} className="text-[11px] uppercase tracking-[0.22em] text-red-300">убрать</button>
        </div>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => fileInputRef.current?.click()} className="px-4 py-2.5 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black">
          Загрузить файл
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={(e) => onFile(e.target.files?.[0])}
          className="hidden"
        />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onValue(e.target.value)}
          placeholder="…или вставьте URL"
          className="flex-1 min-w-[180px] bg-[#0a0a0a] border border-white/10 rounded-sm p-2 text-sm"
        />
      </div>
    </div>
  );
}

function InvitationsList() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/invitations`, { headers: authHeaders() });
      setItems(data);
    } catch {
      toast.error("Не удалось загрузить");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить заявку?")) return;
    await axios.delete(`${API}/admin/invitations/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <div data-testid="admin-invitations" className="space-y-6">
      <h2 className="font-display uppercase text-3xl">Заявки на премьеру ({items.length})</h2>
      <div className="border border-white/10 divide-y divide-white/10">
        {items.length === 0 && <p className="p-6 text-white/40">Ещё нет заявок.</p>}
        {items.map((i) => (
          <div key={i.id} className="p-4 grid grid-cols-12 gap-3 items-baseline">
            <p className="col-span-12 sm:col-span-3 font-display">{i.name}</p>
            <p className="col-span-12 sm:col-span-3 text-white/70">{i.email}</p>
            <p className="col-span-6 sm:col-span-2 text-white/50">{i.phone || "—"}</p>
            <p className="col-span-6 sm:col-span-2 text-white/50">{i.company || "—"}</p>
            <p className="col-span-12 sm:col-span-2 font-mono-jb text-[10px] tracking-[0.22em] text-white/40 text-right">
              {new Date(i.created_at).toLocaleDateString("ru-RU")}
            </p>
            {i.message && <p className="col-span-12 text-white/60 italic text-sm pl-4 border-l border-white/10">{i.message}</p>}
            <button onClick={() => remove(i.id)} className="col-span-12 sm:col-span-1 ml-auto px-3 py-1 text-[11px] text-red-300 border border-red-500/30">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}


function CallbacksList() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/callbacks`, { headers: authHeaders() });
      setItems(data);
    } catch {
      toast.error("Не удалось загрузить");
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Удалить заявку?")) return;
    await axios.delete(`${API}/admin/callbacks/${id}`, { headers: authHeaders() });
    load();
  };

  return (
    <div data-testid="admin-callbacks" className="space-y-6">
      <h2 className="font-display uppercase text-3xl">Заявки на звонок ({items.length})</h2>
      <div className="border border-white/10 divide-y divide-white/10">
        {items.length === 0 && <p className="p-6 text-white/40">Ещё нет заявок.</p>}
        {items.map((i) => (
          <div key={i.id} className="p-4 grid grid-cols-12 gap-3 items-baseline">
            <p className="col-span-12 sm:col-span-3 font-display">{i.name || "—"}</p>
            <p className="col-span-12 sm:col-span-4 text-white/85 font-mono-jb">{i.phone}</p>
            <p className="col-span-12 sm:col-span-3 font-mono-jb text-[10px] tracking-[0.22em] text-white/40 text-right">
              {new Date(i.created_at).toLocaleString("ru-RU")}
            </p>
            <button onClick={() => remove(i.id)} className="col-span-12 sm:col-span-2 ml-auto px-3 py-1 text-[11px] text-red-300 border border-red-500/30">×</button>
          </div>
        ))}
      </div>
    </div>
  );
}


const SECTION_LABELS = {
  studio:  "О нас (Impressions)",
  skills:  "Доказательства навыков (SkillsProof)",
  method:  "Наш метод (Method)",
  work:    "Портфолио / Работы",
  team:    "Команда",
  clients: "Клиенты + Отзывы",
};
const ALL_SECTIONS = ["studio", "skills", "method", "work", "team", "clients"];

function StructureEditor() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/site`, { headers: authHeaders() })
      .then((r) => {
        const order = (r.data.section_order && r.data.section_order.length)
          ? r.data.section_order
          : [...ALL_SECTIONS];
        // make sure no section is missing
        const full = [...order, ...ALL_SECTIONS.filter((id) => !order.includes(id))];
        const enabled = { ...Object.fromEntries(ALL_SECTIONS.map((id) => [id, true])), ...(r.data.section_enabled || {}) };
        setData({ section_order: full, section_enabled: enabled });
      })
      .catch(() => toast.error("Не удалось загрузить"));
  }, []);

  const move = (idx, dir) => {
    setData((d) => {
      const arr = [...d.section_order];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return d;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...d, section_order: arr };
    });
  };
  const toggle = (id) => {
    setData((d) => ({ ...d, section_enabled: { ...d.section_enabled, [id]: !d.section_enabled[id] } }));
  };
  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site`,
        { section_order: data.section_order, section_enabled: data.section_enabled },
        { headers: authHeaders() });
      toast.success("Структура сохранена");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Ошибка сохранения");
    } finally { setSaving(false); }
  };

  if (!data) return <p className="text-white/50">Загружаем…</p>;

  return (
    <div data-testid="admin-structure-editor" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display uppercase text-3xl">Структура страницы</h2>
          <p className="mt-2 text-white/55 text-sm">Включайте/отключайте блоки и меняйте их порядок. Hero и Footer всегда видимы.</p>
        </div>
        <button data-testid="structure-save" onClick={save} disabled={saving} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold">
          {saving ? "Сохраняем…" : "Сохранить"}
        </button>
      </div>

      <ul className="border border-white/10 divide-y divide-white/10">
        {data.section_order.map((id, i) => (
          <li key={id} data-testid={`structure-row-${id}`} className="p-4 grid grid-cols-12 items-center gap-3">
            <span className="col-span-1 font-mono-jb text-[10px] tracking-[0.22em] text-white/40">
              {String(i + 1).padStart(2, "0")}
            </span>
            <span className="col-span-7 sm:col-span-6 font-display">
              {SECTION_LABELS[id] || id}
            </span>
            <div className="col-span-4 sm:col-span-3 flex gap-1">
              <button
                data-testid={`structure-up-${id}`}
                onClick={() => move(i, -1)}
                disabled={i === 0}
                className="px-3 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black disabled:opacity-30"
              >↑</button>
              <button
                data-testid={`structure-down-${id}`}
                onClick={() => move(i, 1)}
                disabled={i === data.section_order.length - 1}
                className="px-3 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black disabled:opacity-30"
              >↓</button>
            </div>
            <label className="col-span-12 sm:col-span-2 flex items-center justify-end gap-3 cursor-pointer">
              <span className={`font-mono-jb text-[10px] uppercase tracking-[0.22em] ${data.section_enabled[id] ? "text-[#a78bfa]" : "text-white/35"}`}>
                {data.section_enabled[id] ? "включён" : "скрыт"}
              </span>
              <input
                type="checkbox"
                data-testid={`structure-toggle-${id}`}
                checked={!!data.section_enabled[id]}
                onChange={() => toggle(id)}
                className="h-5 w-5 accent-[#8b5cf6]"
              />
            </label>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ArticlesEditor() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/articles`, { headers: authHeaders() });
      setItems(data);
    } catch { toast.error("Не удалось загрузить"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ slug: "", title: "", subtitle: "", cover_url: "", body: "", meta_description: "", _new: true });

  const remove = async (id) => {
    if (!window.confirm("Удалить статью?")) return;
    await axios.delete(`${API}/admin/articles/${id}`, { headers: authHeaders() });
    toast.success("Удалено"); load();
  };

  return (
    <div data-testid="admin-articles" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display uppercase text-3xl">Статьи</h2>
          <p className="mt-2 text-white/55 text-sm">Внутренние страницы для SEO и расшифровок «подробнее». Текст в Markdown.</p>
        </div>
        <button data-testid="article-new" onClick={startNew} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold">
          + Новая статья
        </button>
      </div>

      <ul className="border border-white/10 divide-y divide-white/10">
        {items.length === 0 && <li className="p-6 text-white/40">Ещё нет статей.</li>}
        {items.map((a) => (
          <li key={a.id} className="p-4 flex items-center gap-4">
            <div className="w-16 h-16 bg-[#101010] border border-white/10 overflow-hidden shrink-0">
              {a.cover_url ? <img src={a.cover_url} alt="" className="w-full h-full object-cover" /> : null}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display uppercase truncate">{a.title}</p>
              <p className="font-mono-jb text-[11px] uppercase tracking-[0.22em] text-white/40 truncate">
                /article/{a.slug}
              </p>
            </div>
            <a href={`/article/${a.slug}`} target="_blank" rel="noreferrer" className="px-3 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black">Открыть</a>
            <button onClick={() => setEditing(a)} className="px-3 py-2 border border-white/15 text-[11px] uppercase tracking-[0.22em] hover:bg-white hover:text-black">Изм.</button>
            <button onClick={() => remove(a.id)} className="px-3 py-2 border border-red-500/30 text-red-300 text-[11px] uppercase tracking-[0.22em] hover:bg-red-500 hover:text-white">×</button>
          </li>
        ))}
      </ul>

      {editing && (
        <ArticleDialog
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function ArticleDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onCoverFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Файл больше 8 МБ"); return; }
    const dataUrl = await fileToDataURL(file);
    const small = await downscaleImage(dataUrl, 1600, 0.85);
    update("cover_url", small);
  };

  const slugify = (s) => s.toLowerCase().trim().replace(/[^a-z0-9а-яё]+/gi, "-").replace(/^-+|-+$/g, "");

  const save = async () => {
    if (!form.slug || !form.title) { toast.error("Заполните slug и заголовок"); return; }
    setSaving(true);
    const payload = {
      slug: form.slug, title: form.title, subtitle: form.subtitle || "",
      cover_url: form.cover_url || "", body: form.body || "", meta_description: form.meta_description || "",
    };
    try {
      if (form._new || !form.id) {
        await axios.post(`${API}/admin/articles`, payload, { headers: authHeaders() });
      } else {
        await axios.put(`${API}/admin/articles/${form.id}`, payload, { headers: authHeaders() });
      }
      toast.success("Сохранено");
      onSaved();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : "Ошибка");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0a0a0a] border border-white/15 max-w-3xl w-full p-8 my-8" data-testid="admin-article-dialog">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display uppercase text-xl">{form._new ? "Новая статья" : "Редактировать статью"}</h3>
          <button onClick={onClose} className="text-white/60">×</button>
        </div>
        <div className="space-y-5">
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Slug (URL) <span className="text-[#a78bfa]">*</span></span>
            <div className="mt-2 flex gap-2">
              <input value={form.slug || ""} onChange={(e) => update("slug", e.target.value.toLowerCase())} placeholder="led-arenas" className="flex-1 bg-[#0a0a0a] border border-white/10 rounded-sm p-3 font-mono-jb" />
              <button type="button" onClick={() => update("slug", slugify(form.title || ""))} className="px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.22em] hover:bg-white hover:text-black whitespace-nowrap">
                из заголовка
              </button>
            </div>
            <p className="mt-1 text-white/35 text-xs">Открывается по адресу <span className="font-mono-jb">/article/{form.slug || "your-slug"}</span></p>
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Заголовок <span className="text-[#a78bfa]">*</span></span>
            <input value={form.title || ""} onChange={(e) => update("title", e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Подзаголовок</span>
            <input value={form.subtitle || ""} onChange={(e) => update("subtitle", e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Обложка</span>
            <ImageField value={form.cover_url} onValue={(v) => update("cover_url", v)} onFile={onCoverFile} />
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Тело (Markdown)</span>
            <textarea
              rows={14}
              value={form.body || ""}
              onChange={(e) => update("body", e.target.value)}
              placeholder={"# Заголовок\n\nАбзац **жирно** и *курсив*.\n\n- список\n- ещё пункт"}
              className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3 font-mono-jb text-sm leading-relaxed"
            />
            <p className="mt-1 text-white/35 text-xs">
              Поддержка Markdown: `# заголовки`, `**жирный**`, `[ссылка](url)`, списки, цитаты.
            </p>
          </label>
          <label className="block">
            <span className="font-mono-jb text-[10px] uppercase tracking-[0.22em] text-white/40">Meta description (для SEO)</span>
            <textarea rows={2} value={form.meta_description || ""} onChange={(e) => update("meta_description", e.target.value)} className="mt-2 w-full bg-[#0a0a0a] border border-white/10 rounded-sm p-3" />
          </label>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-3 border border-white/15 text-[11px] uppercase tracking-[0.22em]">Отмена</button>
          <button onClick={save} disabled={saving} className="bbf-btn-primary px-5 py-3 uppercase text-[11px] tracking-[0.22em] font-bold disabled:opacity-60" data-testid="article-save">
            {saving ? "Сохраняем…" : "Сохранить"}
          </button>
        </div>
      </div>
    </div>
  );
}
